import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { courseService } from '../services/api';
import { Course } from '../types/course';
import GolfCourseMap from '../components/GolfCourseMapV2';
import Breadcrumbs from '../components/Breadcrumbs';
import BackButton from '../components/BackButton';

export default function CourseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(true);

  // Tee color mapping for scorecard display
  const teeColorMap: { [key: string]: { bg: string; text: string; border?: string } } = {
    'Black': { bg: 'bg-gray-900', text: 'text-white' },
    'Blue': { bg: 'bg-blue-600', text: 'text-white' },
    'White': { bg: 'bg-white', text: 'text-gray-900', border: 'border border-gray-300' },
    'Gold': { bg: 'bg-yellow-500', text: 'text-gray-900' },
    'Yellow': { bg: 'bg-yellow-400', text: 'text-gray-900' },
    'Red': { bg: 'bg-red-600', text: 'text-white' },
    'Green': { bg: 'bg-green-600', text: 'text-white' }
  };

  useEffect(() => {
    const fetchCourse = async () => {
      if (!id) {
        setError('Course ID not found');
        setLoading(false);
        return;
      }

      try {
        const data = await courseService.getCourse(id);
        setCourse(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-golf-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading course...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Course Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <Link to="/courses" className="bg-gradient-to-r from-golf-500 to-emerald-500 hover:from-golf-600 hover:to-emerald-600 text-white px-6 py-2 rounded-lg transition-all duration-300">
            Back to Courses
          </Link>
        </div>
      </div>
    );
  }

  const teeTypes = Array.from(
    new Set(course.holes.flatMap(hole => hole.yardages.map(y => y.tee)))
  );
  const sortedHoles = [...course.holes].sort((left, right) => left.number - right.number);
  const contributionSeries = sessionStorage.getItem(`course-contribution-for-course:${course.id}`);

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          items={[
            { label: 'Courses', href: '/courses' },
            { label: course.name }
          ]}
        />
        
        {/* Course Header */}
        <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">{course.name}</h1>
              <p className="text-gray-600 dark:text-gray-400 flex items-center mt-1">
                📍 {course.location}
              </p>
              {course.latitude && course.longitude && (
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  GPS: {course.latitude.toFixed(6)}, {course.longitude.toFixed(6)}
                </p>
              )}
            </div>
            <div className="text-right">
              <div className="bg-gradient-to-r from-golf-100 to-emerald-100 dark:from-golf-900 dark:to-emerald-900 px-4 py-2 rounded-lg">
                <span className="text-lg font-bold text-golf-800 dark:text-golf-200">
                  Par {course.par}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {course.holes.length} holes
              </p>
            </div>
          </div>
        </div>

        {/* Map Toggle */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Course Layout</h2>
          <button
            onClick={() => setShowMap(!showMap)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {showMap ? '📋 Show Scorecard' : '🗺️ Show Map'}
          </button>
        </div>

        {/* Course Map */}
        {showMap && (
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
              Satellite View with Hole Markers
            </h3>
            <GolfCourseMap course={course} />
            <div className="mt-4 flex items-center justify-center space-x-6 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                <span>Tee Boxes</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-400 rounded-full mr-2 border-2 border-white"></div>
                <span>Greens</span>
              </div>
            </div>
          </div>
        )}

        {/* Scorecard View */}
        {!showMap && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden mb-6">
            <div className="p-6 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                Scorecard - All Tees
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Official distances in {course.distanceUnit ?? 'yards'}</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Hole</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Par</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">HCP</th>
                    {teeTypes.map(tee => {
                      const colors = teeColorMap[tee] || { bg: 'bg-gray-400', text: 'text-white' };
                      return (
                        <th key={tee} className="px-4 py-3 text-center">
                          <span className={`inline-block px-3 py-1 rounded-md text-xs font-bold ${colors.bg} ${colors.text} ${colors.border || ''}`}>
                            {tee}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* Front 9 */}
                  {sortedHoles.filter(h => h.number <= 9).map((hole) => (
                    <tr key={hole.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-200">
                        {hole.number}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400">
                        {hole.par}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400">
                        {hole.handicap}
                      </td>
                      {teeTypes.map(tee => {
                        const yardage = hole.yardages.find(y => y.tee === tee);
                        const colors = teeColorMap[tee] || { bg: 'bg-gray-400', text: 'text-white' };
                        // Create a lighter version of the tee color for the cell background
                        const bgOpacity = tee === 'White' ? 'bg-gray-50' : colors.bg.replace('600', '50').replace('500', '50').replace('400', '50').replace('900', '100');
                        return (
                          <td key={tee} className={`px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 ${bgOpacity} dark:bg-opacity-10`}>
                            {yardage ? `${yardage.distance}m` : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Front 9 Subtotal */}
                  <tr className="bg-gray-100 dark:bg-gray-700 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
                    <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">OUT</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-800 dark:text-gray-200">
                      {course.holes.filter(h => h.number <= 9).reduce((sum, hole) => sum + hole.par, 0)}
                    </td>
                    <td className="px-4 py-3"></td>
                    {teeTypes.map(tee => {
                      const front9Total = course.holes.filter(h => h.number <= 9).reduce((sum, hole) => {
                        const yardage = hole.yardages.find(y => y.tee === tee);
                        return sum + (yardage?.distance || 0);
                      }, 0);
                      return (
                        <td key={tee} className="px-4 py-3 text-center text-sm font-bold text-gray-800 dark:text-gray-200">
                          {front9Total > 0 ? `${front9Total}m` : '-'}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Back 9 */}
                  {sortedHoles.filter(h => h.number > 9).map((hole) => (
                    <tr key={hole.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-200">
                        {hole.number}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400">
                        {hole.par}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-400">
                        {hole.handicap}
                      </td>
                      {teeTypes.map(tee => {
                        const yardage = hole.yardages.find(y => y.tee === tee);
                        const colors = teeColorMap[tee] || { bg: 'bg-gray-400', text: 'text-white' };
                        // Create a lighter version of the tee color for the cell background
                        const bgOpacity = tee === 'White' ? 'bg-gray-50' : colors.bg.replace('600', '50').replace('500', '50').replace('400', '50').replace('900', '100');
                        return (
                          <td key={tee} className={`px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 ${bgOpacity} dark:bg-opacity-10`}>
                            {yardage ? `${yardage.distance}m` : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Back 9 Subtotal */}
                  <tr className="bg-gray-100 dark:bg-gray-700 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
                    <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200">IN</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-800 dark:text-gray-200">
                      {course.holes.filter(h => h.number > 9).reduce((sum, hole) => sum + hole.par, 0)}
                    </td>
                    <td className="px-4 py-3"></td>
                    {teeTypes.map(tee => {
                      const back9Total = course.holes.filter(h => h.number > 9).reduce((sum, hole) => {
                        const yardage = hole.yardages.find(y => y.tee === tee);
                        return sum + (yardage?.distance || 0);
                      }, 0);
                      return (
                        <td key={tee} className="px-4 py-3 text-center text-sm font-bold text-gray-800 dark:text-gray-200">
                          {back9Total > 0 ? `${back9Total}m` : '-'}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Grand Total */}
                  <tr className="bg-gray-200 dark:bg-gray-800 font-bold border-t-2 border-gray-400 dark:border-gray-500">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">TOTAL</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-900 dark:text-gray-100">
                      {course.par}
                    </td>
                    <td className="px-4 py-3"></td>
                    {teeTypes.map(tee => {
                      const total = course.holes.reduce((sum, hole) => {
                        const yardage = hole.yardages.find(y => y.tee === tee);
                        return sum + (yardage?.distance || 0);
                      }, 0);
                      const colors = teeColorMap[tee] || { bg: 'bg-gray-400', text: 'text-white' };
                      return (
                        <td key={tee} className="px-4 py-3 text-center">
                          <span className={`inline-block px-3 py-1 rounded-md text-sm font-bold ${colors.bg} ${colors.text} ${colors.border || ''}`}>
                            {total > 0 ? `${total}m` : '-'}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <BackButton to="/courses" label="Back to Courses" />
            <div className="space-x-4">
              <Link 
                to={`/rounds/new?courseId=${course.id}`}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                🎯 Play This Course
              </Link>
              <Link 
                to={`/courses/${course.id}/map-editor`}
                className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                📍 Edit GPS Coordinates
              </Link>
              <Link to={`/courses/${course.id}/edit`} className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                ✏️ Edit Course
              </Link>
              {contributionSeries && <Link to={`/courses/contribute?series=${contributionSeries}`} className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">Correct scorecard</Link>}
            </div>
          </div>
        </div>
        
        {/* Bottom Navigation */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <BackButton to="/dashboard" label="Back to Dashboard" />
          <div className="flex gap-3">
            <Link
              to="/courses"
              className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-6 py-2 rounded-lg font-medium transition-colors"
            >
              View All Courses
            </Link>
            <Link
              to="/courses/new"
              className="bg-gradient-to-r from-golf-500 to-emerald-500 hover:from-golf-600 hover:to-emerald-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Add New Course
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
